import { useEffect, useRef, useMemo, useState, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import MarioBlueLaunch from '../../assets/images/MarioBlueLaunch.png'
import MarioGoldLaunch from '../../assets/images/MarioGoldLaunch.png'
import './index.scss'

gsap.registerPlugin(ScrollTrigger)

const MOBILE_BP = 900

const MILESTONES = [
  {
    year: '2015',
    title: 'Turlock Computers',
    role: 'Technician',
    type: 'job',
    description:
      'First steps in tech — diagnosing, repairing, and building computers. The place where curiosity about how machines work turned into a career.',
  },
  {
    year: '2021',
    title: 'Apple',
    role: 'Specialist',
    type: 'job',
    description:
      'Learned what great product experience feels like from the inside. Apple\'s obsession with thoughtful design still shapes how I build software today.',
  },
  {
    year: '2021',
    title: 'ISSNAF',
    role: 'Software Engineer',
    type: 'job',
    description:
      'Built software for the Italian Scientists and Scholars in North America Foundation — real shipping code, real stakeholders, real deadlines.',
  },
  {
    year: '2022',
    title: 'UC Merced',
    role: 'B.S. Computer Science & Engineering',
    type: 'school',
    description:
      'Foundations in systems, algorithms, and software engineering. Graduated with a clear path toward full-stack and machine learning work.',
  },
  {
    year: '2023',
    title: 'C2 Education',
    role: 'Computer Science & Calculus Teacher',
    type: 'job',
    description:
      'Taught CS and calculus to high schoolers. Nothing sharpens your own understanding faster than having to explain it clearly to someone else.',
  },
  {
    year: '2024',
    title: 'Volkswagen Group of America',
    role: 'Software Engineer Intern — IECC',
    type: 'job',
    description:
      'Joined the Innovation & Engineering Center California as an intern, contributing to full-stack features and internal tooling for connected vehicle research.',
  },
  {
    year: '2025',
    title: 'San José State University',
    role: 'M.S. Computer Science',
    type: 'school',
    description:
      'Completed a master\'s focused on machine learning, data-driven systems, and applied research. Thesis benchmarked ML across Apple Silicon, CUDA, and neuromorphic hardware.',
  },
  {
    year: '2025 — Present',
    title: 'Volkswagen Group of America',
    role: 'Full-Stack Software Engineer — IECC',
    type: 'job',
    description:
      'Back at IECC, now full-time, building the full stack for connected vehicle and innovation programs.',
  },
]

// Layout presets. Positions are FRACTIONS of the timeline container width so
// the SVG viewBox can match the container 1:1 (no horizontal stretching, which
// would otherwise squish the Mario star glyphs into vertical ellipses).
const DESKTOP_LAYOUT = {
  CX_F: 0.5,
  AMP_F: 0.082,
  WAVE_CYCLES: 3.5,
  ROW_HEIGHT: 320,
  HEADER_PAD: 140,
  FOOTER_PAD: 220,
}

const MOBILE_LAYOUT = {
  CX_F: 0.18,
  AMP_F: 0.09,
  WAVE_CYCLES: 3.5,
  ROW_HEIGHT: 260,
  HEADER_PAD: 110,
  FOOTER_PAD: 190,
}

const NOVA_THRESHOLD = 0.95

const SPARK_POOL = 64
const SPARKS_PER_TICK = 4
const SPARK_LIFE_MIN = 45
const SPARK_LIFE_MAX = 95
const SPARK_GRAVITY = 0.09
const SPARK_DRAG = 0.985
const SPARK_COLORS = ['#ffffff', '#ffd700', '#b8a4ff']

const Experience = () => {
  const sectionRef = useRef(null)
  const timelineRef = useRef(null)
  const pathRef = useRef(null)
  const cometRef = useRef(null)
  const sparkElRefs = useRef([])
  const sparksRef = useRef([])
  const launchRefs = useRef([])
  const nodeRefs = useRef([])
  const supernovaRef = useRef(null)

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BP
  )
  const [containerW, setContainerW] = useState(900)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BP)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useLayoutEffect(() => {
    if (!timelineRef.current) return
    const el = timelineRef.current
    const update = () => {
      const w = el.getBoundingClientRect().width
      if (w > 0) setContainerW(w)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const L = useMemo(
    () => (isMobile ? MOBILE_LAYOUT : DESKTOP_LAYOUT),
    [isMobile]
  )

  const VIEW_W = containerW
  const CX = VIEW_W * L.CX_F
  const AMP = VIEW_W * L.AMP_F
  const totalH = L.HEADER_PAD + MILESTONES.length * L.ROW_HEIGHT + L.FOOTER_PAD

  const nodes = useMemo(
    () =>
      MILESTONES.map((m, i) => {
        const y = L.HEADER_PAD + i * L.ROW_HEIGHT + L.ROW_HEIGHT / 2
        const x = CX + Math.sin((y / totalH) * Math.PI * 2 * L.WAVE_CYCLES) * AMP
        return {
          ...m,
          x,
          y,
          side: isMobile ? 'right' : i % 2 === 0 ? 'left' : 'right',
        }
      }),
    [L, CX, AMP, totalH, isMobile]
  )

  const pathD = useMemo(() => {
    const steps = 90
    const pts = []
    for (let i = 0; i <= steps; i++) {
      const y = (i / steps) * totalH
      const x = CX + Math.sin((y / totalH) * Math.PI * 2 * L.WAVE_CYCLES) * AMP
      pts.push({ x, y })
    }
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i].x + pts[i + 1].x) / 2
      const my = (pts[i].y + pts[i + 1].y) / 2
      d += ` Q ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)}`
    }
    const last = pts[pts.length - 1]
    d += ` L ${last.x.toFixed(1)} ${last.y.toFixed(1)}`
    return d
  }, [CX, AMP, totalH, L.WAVE_CYCLES])

  const END_Y = totalH
  const END_X = CX + Math.sin(Math.PI * 2 * L.WAVE_CYCLES) * AMP

  const stars = useMemo(() => {
    let seed = 1337
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280
      return seed / 233280
    }
    return Array.from({ length: 28 }, () => ({
      x: rand() * VIEW_W,
      y: rand() * totalH,
      r: 0.5 + rand() * 1.8,
      delay: rand() * 5,
      dur: 2.4 + rand() * 4.2,
    }))
  }, [VIEW_W, totalH])

  const novaStars = useMemo(() => {
    let seed = 7777
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280
      return seed / 233280
    }
    return Array.from({ length: 16 }, (_, i) => {
      const angle = (i / 16) * Math.PI * 2 + (rand() - 0.5) * 0.5
      const dist = 75 + rand() * 95
      return {
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        r: 0.9 + rand() * 1.6,
        delay: 0.25 + rand() * 0.85,
        type: rand() > 0.5 ? 'gold' : 'purple',
      }
    })
  }, [])

  useEffect(() => {
    const scroller = document.querySelector('.page')
    if (!scroller) return
    const path = pathRef.current
    if (!path) return

    const length = path.getTotalLength()
    path.style.strokeDasharray = `${length}`
    path.style.strokeDashoffset = `${length}`

    if (sparksRef.current.length === 0) {
      sparksRef.current = Array.from({ length: SPARK_POOL }, () => ({
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        age: 0,
        life: 0,
        size: 1,
        active: false,
        colorOffset: 0,
        phaseIdx: -1,
      }))
    }

    const nodeProgs = nodes.map((n) => {
      let best = 0
      let bestDist = Infinity
      const samples = 400
      for (let i = 0; i <= samples; i++) {
        const p = i / samples
        const pt = path.getPointAtLength(length * p)
        const dx = pt.x - n.x
        const dy = pt.y - n.y
        const dist = dx * dx + dy * dy
        if (dist < bestDist) {
          bestDist = dist
          best = p
        }
      }
      return best
    })

    const ctx = gsap.context(() => {
      let pendingProg = 0
      let lastProg = -1
      let rafScheduled = false
      const retrigger = (el, cls) => {
        if (!el) return
        el.classList.remove(cls)
        void el.getBoundingClientRect()
        el.classList.add(cls)
      }
      const applyProg = () => {
        rafScheduled = false
        const prog = pendingProg
        path.style.strokeDashoffset = `${length * (1 - prog)}`

        const cometVisible = prog > 0.002 && prog < 0.998
        let headX = 0
        let headY = 0
        let headAngle = 0

        if (cometRef.current) {
          const pt = path.getPointAtLength(length * prog)
          const ahead = path.getPointAtLength(Math.max(0, length * prog - 6))
          headAngle = (Math.atan2(pt.y - ahead.y, pt.x - ahead.x) * 180) / Math.PI
          headX = pt.x
          headY = pt.y
          cometRef.current.style.transform = `translate3d(${pt.x}px, ${pt.y}px, 0) rotate(${headAngle}deg)`
          cometRef.current.style.opacity = cometVisible ? 1 : 0
        }

        if (cometVisible) {
          const sparks = sparksRef.current
          const behind = (headAngle * Math.PI) / 180 + Math.PI
          let spawned = 0
          for (let i = 0; i < sparks.length && spawned < SPARKS_PER_TICK; i++) {
            const p = sparks[i]
            if (p.active) continue
            const spread = (Math.random() - 0.5) * 1.8
            const dir = behind + spread
            const speed = 0.6 + Math.random() * 2.4
            p.x = headX
            p.y = headY
            p.vx = Math.cos(dir) * speed
            p.vy = Math.sin(dir) * speed - 0.2
            p.age = 0
            p.life =
              SPARK_LIFE_MIN + Math.random() * (SPARK_LIFE_MAX - SPARK_LIFE_MIN)
            p.size = 0.7 + Math.random() * 0.8
            p.colorOffset = Math.floor(Math.random() * SPARK_COLORS.length)
            p.phaseIdx = -1
            p.active = true
            spawned++
          }
        }

        if (lastProg >= 0) {
          nodeProgs.forEach((np, i) => {
            const crossed =
              (lastProg < np && prog >= np) ||
              (lastProg > np && prog <= np)
            if (crossed) {
              retrigger(launchRefs.current[i], 'exp-launching')
              retrigger(cometRef.current, 'exp-boosting')
            }
          })
        }

        if (lastProg < NOVA_THRESHOLD && prog >= NOVA_THRESHOLD) {
          retrigger(supernovaRef.current, 'exp-supernova-active')
        }

        lastProg = prog
      }

      ScrollTrigger.create({
        trigger: sectionRef.current,
        scroller,
        start: 'top center',
        end: 'bottom bottom',
        onUpdate: (self) => {
          pendingProg = self.progress
          if (!rafScheduled) {
            rafScheduled = true
            requestAnimationFrame(applyProg)
          }
        },
      })


      nodeRefs.current.forEach((el) => {
        if (!el) return
        gsap.fromTo(
          el,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: el,
              scroller,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        )
      })

      ScrollTrigger.refresh()
    }, sectionRef)

    let sparkRafId
    const sparkTick = () => {
      const sparks = sparksRef.current
      for (let i = 0; i < sparks.length; i++) {
        const p = sparks[i]
        const el = sparkElRefs.current[i]
        if (!el) continue
        if (!p.active) {
          if (el.style.opacity !== '0') el.style.opacity = 0
          continue
        }
        p.age += 1
        p.vx *= SPARK_DRAG
        p.vy = p.vy * SPARK_DRAG + SPARK_GRAVITY
        p.x += p.vx
        p.y += p.vy
        const t = p.age / p.life
        if (t >= 1) {
          p.active = false
          el.style.opacity = 0
          continue
        }
        const fade = (1 - t) * (1 - t)
        const vAngle = (Math.atan2(p.vy, p.vx) * 180) / Math.PI
        el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) rotate(${vAngle}deg) scale(${p.size})`
        el.style.opacity = fade
        const phaseIdx = Math.min(SPARK_COLORS.length - 1, Math.floor(t * SPARK_COLORS.length))
        if (phaseIdx !== p.phaseIdx) {
          p.phaseIdx = phaseIdx
          const c = (p.colorOffset + phaseIdx) % SPARK_COLORS.length
          const streak = el.firstChild
          const head = el.lastChild
          if (streak) streak.setAttribute('fill', `url(#sparkStreak${c})`)
          if (head) head.setAttribute('fill', SPARK_COLORS[c])
        }
      }
      sparkRafId = requestAnimationFrame(sparkTick)
    }
    sparkRafId = requestAnimationFrame(sparkTick)

    return () => {
      cancelAnimationFrame(sparkRafId)
      ctx.revert()
    }
  }, [nodes, pathD, isMobile])

  return (
    <section ref={sectionRef} className="experience-section">
      <div className="exp-header">
        <h1 className="exp-title">Experience</h1>
        <p className="exp-subtitle">A constellation chart through time.</p>
        <div className="exp-legend">
          <span className="legend-item">
            <img
              src={MarioBlueLaunch}
              alt=""
              className="legend-glyph-img legend-glyph-school"
              aria-hidden
            />
            Education
          </span>
          <span className="legend-item">
            <img
              src={MarioGoldLaunch}
              alt=""
              className="legend-glyph-img legend-glyph-job"
              aria-hidden
            />
            Work
          </span>
        </div>
      </div>

      <div ref={timelineRef} className="exp-timeline" style={{ height: totalH }}>
        <div className="exp-connector" aria-hidden="true">
          <span className="exp-connector-pulse" />
        </div>
        <svg
          className="exp-spine"
          viewBox={`0 0 ${VIEW_W} ${totalH}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id="spineGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#d10000" />
              <stop offset="50%" stopColor="#ff7a3d" />
              <stop offset="100%" stopColor="#ffd700" />
            </linearGradient>
            <radialGradient id="cometGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="40%" stopColor="#ffd700" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ffd700" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="cometGlowSchool" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="40%" stopColor="#b8a4ff" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#6b4dff" stopOpacity="0" />
            </radialGradient>
            {SPARK_COLORS.map((color, i) => (
              <linearGradient
                key={i}
                id={`sparkStreak${i}`}
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <stop offset="0%" stopColor={color} stopOpacity="0" />
                <stop offset="60%" stopColor={color} stopOpacity="0.45" />
                <stop offset="100%" stopColor={color} stopOpacity="1" />
              </linearGradient>
            ))}
            <radialGradient id="planetGrad" cx="35%" cy="35%" r="70%">
              <stop offset="0%" stopColor="#b8a4ff" />
              <stop offset="60%" stopColor="#6b4dff" />
              <stop offset="100%" stopColor="#2a1a6b" />
            </radialGradient>
            <radialGradient id="novaFlash" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="30%" stopColor="#ffd700" stopOpacity="0.95" />
              <stop offset="65%" stopColor="#ff7a3d" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#d10000" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="novaNebula" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#b8a4ff" stopOpacity="0.6" />
              <stop offset="35%" stopColor="#6b4dff" stopOpacity="0.32" />
              <stop offset="75%" stopColor="#2a1a6b" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#2a1a6b" stopOpacity="0" />
            </radialGradient>
          </defs>

          <g className="exp-stardust" aria-hidden="true">
            {stars.map((s, i) => (
              <circle
                key={i}
                className="exp-star"
                cx={s.x}
                cy={s.y}
                r={s.r}
                style={{
                  animationDelay: `${s.delay}s`,
                  animationDuration: `${s.dur}s`,
                }}
              />
            ))}
          </g>

          <path
            d={pathD}
            className="exp-spine-bg"
            fill="none"
            stroke="rgba(255, 255, 255, 0.08)"
            strokeWidth="2"
            strokeDasharray="4 6"
            vectorEffect="non-scaling-stroke"
          />

          <path
            ref={pathRef}
            className="exp-spine-drawn"
            d={pathD}
            fill="none"
            stroke="url(#spineGrad)"
            strokeWidth="2.5"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />

          {/* SPARKLER: Sparks fall off the comet as it moves */}
          <g className="exp-sparks" aria-hidden="true">
            {Array.from({ length: SPARK_POOL }).map((_, i) => (
              <g
                key={i}
                ref={(el) => (sparkElRefs.current[i] = el)}
                className="exp-spark"
                style={{ opacity: 0 }}
              >
                <ellipse rx="7" ry="1.1" fill="url(#sparkStreak0)" />
                <circle r="1.3" fill="#ffffff" />
              </g>
            ))}
          </g>

          {/* COMET HEAD: Bright core + gold halo */}
          <g ref={cometRef} className="exp-comet" style={{ opacity: 0 }}>
            <ellipse cx="-12" cy="0" rx="18" ry="4" fill="url(#cometGlow)" opacity="0.7" />
            <circle r="12" fill="url(#cometGlow)" />
            <circle r="3" fill="#ffffff" />
          </g>

          {nodes.map((n, i) => (
            <g
              key={i}
              className={`exp-node-glyph exp-node-${n.type}`}
              transform={`translate(${n.x} ${n.y})`}
            >
              <circle
                className="exp-node-ripple"
                r="12"
                fill="none"
                stroke={n.type === 'school' ? '#b8a4ff' : '#ffd700'}
                strokeWidth="1"
                style={{ animationDelay: `${(i % 4) * 0.6}s` }}
              />
              <circle
                className="exp-node-ripple exp-node-ripple-slow"
                r="12"
                fill="none"
                stroke={n.type === 'school' ? 'rgba(184, 164, 255, 0.45)' : 'rgba(255, 122, 61, 0.5)'}
                strokeWidth="1"
                style={{ animationDelay: `${(i % 4) * 0.6 + 1.8}s` }}
              />
              <circle
                r="18"
                fill={n.type === 'school' ? 'rgba(184, 164, 255, 0.08)' : 'rgba(255, 215, 0, 0.08)'}
              />
              <g className="exp-node-star-spin">
                <image
                  href={n.type === 'school' ? MarioBlueLaunch : MarioGoldLaunch}
                  x="-18"
                  y="-18"
                  width="36"
                  height="36"
                />
              </g>

              {/* LAUNCH BURST: Dynamic colors based on node type */}
              <g
                className="exp-launch-star"
                ref={(el) => (launchRefs.current[i] = el)}
              >
                <circle 
                  className="launch-flash" 
                  r="22" 
                  fill={n.type === 'school' ? "url(#cometGlowSchool)" : "url(#cometGlow)"} 
                />
                <g className="launch-spokes">
                  {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                    <line
                      key={angle}
                      className="launch-spoke"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="-28"
                      stroke={n.type === 'school' ? "#b8a4ff" : "#ffd700"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      transform={`rotate(${angle})`}
                    />
                  ))}
                </g>
                <circle
                  className="launch-ring launch-ring-inner"
                  r="14.4"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                />
                <circle
                  className="launch-ring launch-ring-outer"
                  r="21.6"
                  fill="none"
                  stroke={n.type === 'school' ? "#b8a4ff" : "#ffd700"}
                  strokeWidth="1.2"
                />
                {[0, 60, 120, 180, 240, 300].map((angle) => (
                  <g
                    key={`spark-${angle}`}
                    className="launch-spark-orbit"
                    transform={`rotate(${angle})`}
                  >
                    <circle
                      className="launch-spark"
                      r="1.8"
                      cx="0"
                      cy="-14"
                      fill={n.type === 'school' ? "#b8a4ff" : "#ffd700"}
                    />
                  </g>
                ))}
              </g>
            </g>
          ))}

          {/* SUPERNOVA: End-of-spine cosmic finale */}
          <g
            ref={supernovaRef}
            className="exp-supernova"
            transform={`translate(${END_X} ${END_Y})`}
            aria-hidden="true"
          >
            <circle className="nova-nebula" r="170" fill="url(#novaNebula)" />

            <circle className="nova-ring nova-ring-1" r="20" fill="none" stroke="#ffffff" strokeWidth="2.5" />
            <circle className="nova-ring nova-ring-2" r="20" fill="none" stroke="#ffd700" strokeWidth="2" />
            <circle className="nova-ring nova-ring-3" r="20" fill="none" stroke="#b8a4ff" strokeWidth="1.5" />

            {Array.from({ length: 16 }).map((_, i) => (
              <g key={i} transform={`rotate(${i * 22.5})`}>
                <line
                  className="nova-ray"
                  x1="0"
                  y1="-14"
                  x2="0"
                  y2="-115"
                  stroke={i % 2 === 0 ? '#ffd700' : '#b8a4ff'}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </g>
            ))}

            {novaStars.map((s, i) => (
              <g key={i} transform={`translate(${s.x} ${s.y})`}>
                <g
                  className={`nova-star nova-star-${s.type}`}
                  style={{ animationDelay: `${s.delay}s` }}
                >
                  <circle
                    r={s.r * 2.6}
                    fill={s.type === 'gold' ? 'url(#cometGlow)' : 'url(#cometGlowSchool)'}
                    opacity="0.55"
                  />
                  <circle r={s.r} fill={s.type === 'gold' ? '#ffd700' : '#b8a4ff'} />
                  <circle r={s.r * 0.4} fill="#ffffff" />
                </g>
              </g>
            ))}

            <circle className="nova-flash" r="55" fill="url(#novaFlash)" />
            <circle className="nova-core" r="6" fill="#ffffff" />
          </g>
        </svg>

        <div className="exp-cards">
          {nodes.map((n, i) => {
            const leftPct = (n.x / VIEW_W) * 100
            // On mobile, pin all cards to the right of the spine's rightmost
            // excursion (CX_F + AMP_F) with a little breathing room for the
            // comet halo.
            const mobileLeftPct = (L.CX_F + L.AMP_F) * 100 + 6
            const positionStyle = isMobile
              ? { left: `${mobileLeftPct}%`, right: '14px' }
              : n.side === 'left'
              ? { right: `calc(100% - ${leftPct}% + 40px)` }
              : { left: `calc(${leftPct}% + 40px)` }
            return (
              <div
                key={i}
                ref={(el) => (nodeRefs.current[i] = el)}
                className={`exp-card exp-card-${n.side} exp-card-${n.type}`}
                style={{ top: `${n.y}px`, ...positionStyle }}
              >
                <div className="exp-card-year">{n.year}</div>
                <div className="exp-card-title">{n.title}</div>
                <div className="exp-card-role">{n.role}</div>
                <p className="exp-card-desc">{n.description}</p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default Experience